import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

/**
 * 30 Second Reaction — constants.
 * Difficulty ramps linearly from the "easy" end of the range to the
 * "hard" end as the timer counts down.
 */
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const DURATION_MS = 30_000;
const START_RADIUS = 40;
const END_RADIUS = 20;
const START_INTERVAL_MS = 1200;
const END_INTERVAL_MS = 500;
const COLOR_BG = 0x1a1a2e;
const COLOR_ACCENT = 0xe94560;

/**
 * Callbacks the inner Phaser scene uses to talk back to the runtime
 * wrapper (which owns score + analytics).
 */
interface SceneCallbacks {
  /** User hit the target. */
  targetClick(): void;
  /** User clicked empty space. */
  wrongClick(): void;
  /** A target was spawned (with its radius). */
  targetSpawn(radius: number): void;
  /** The 30s timer reached zero. */
  roundComplete(): void;
  /** User pressed "Play Again" on the game-over screen. */
  retry(): void;
  /** Whether the host has already requested the round to start. */
  wantsStart(): boolean;
}

/**
 * ReactionScene — the actual Phaser gameplay scene. It owns rendering,
 * target spawning, the countdown clock and difficulty ramping, but keeps
 * the score + analytics concerns delegated to the ReactionGame wrapper.
 */
class ReactionScene extends Phaser.Scene {
  private readonly cb: SceneCallbacks;

  private timerText: Phaser.GameObjects.Text | null = null;
  private scoreText: Phaser.GameObjects.Text | null = null;
  private target: Phaser.GameObjects.Arc | null = null;
  private readyOverlay: Phaser.GameObjects.Container | null = null;
  private overOverlay: Phaser.GameObjects.Container | null = null;

  private pendingSpawn: Phaser.Time.TimerEvent | null = null;
  private remainingMs = DURATION_MS;
  private running = false;
  private created = false;

  constructor(cb: SceneCallbacks) {
    super({ key: 'ReactionScene' });
    this.cb = cb;
  }

  create(): void {
    const { width, height } = this.scale;

    // Header: countdown + score.
    this.timerText = this.add
      .text(width / 2, 36, '30', {
        fontSize: '40px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.scoreText = this.add
      .text(width - 70, 36, 'Score: 0', {
        fontSize: '24px',
        color: '#e94560',
      })
      .setOrigin(0.5);

    // Full-screen invisible background: clicking it = wrong click.
    // Phaser only delivers pointer events to the topmost interactive
    // object, so targets on top will never double-fire this handler.
    const background = this.add
      .rectangle(width / 2, height / 2, width, height, COLOR_BG)
      .setInteractive();
    background.on('pointerdown', () => {
      if (this.running) this.cb.wrongClick();
    });

    this.buildReadyOverlay(width, height);
    this.buildGameOverOverlay(width, height);

    this.created = true;

    // If the host requested a start before the scene finished booting,
    // begin the round immediately.
    if (this.cb.wantsStart()) {
      this.beginRound();
    }
  }

  /** Called by the wrapper when start() fires (possibly before create()). */
  requestBegin(): void {
    if (this.created) {
      this.beginRound();
    }
  }

  /** Begin a fresh round: reset clock, show targets. */
  beginRound(): void {
    this.running = true;
    this.remainingMs = DURATION_MS;
    this.hideOverlay(this.readyOverlay);
    this.hideOverlay(this.overOverlay);
    this.spawnTarget();
    this.scheduleNextSpawn(this.currentInterval());
  }

  /** Clear targets / pending spawns (used by retry). */
  resetRound(): void {
    if (this.pendingSpawn) {
      this.pendingSpawn.remove(false);
      this.pendingSpawn = null;
    }
    this.clearTarget();
    this.running = false;
    this.scoreText?.setText('Score: 0');
    this.timerText?.setText('30');
  }

  /** Update the displayed score after a point is scored. */
  setScoreDisplay(score: number): void {
    this.scoreText?.setText(`Score: ${score}`);
  }

  override update(_time: number, delta: number): void {
    if (!this.running) return;
    this.remainingMs -= delta;
    const secondsLeft = Math.max(0, Math.ceil(this.remainingMs / 1000));
    this.timerText?.setText(String(secondsLeft));
    if (this.remainingMs <= 0) {
      this.endRound();
    }
  }

  // ---- internals -------------------------------------------------------

  private progress(): number {
    const t = 1 - this.remainingMs / DURATION_MS;
    return Math.min(1, Math.max(0, t));
  }

  private difficulty(): { radius: number; interval: number } {
    const t = this.progress();
    return {
      radius: START_RADIUS + (END_RADIUS - START_RADIUS) * t,
      interval: START_INTERVAL_MS + (END_INTERVAL_MS - START_INTERVAL_MS) * t,
    };
  }

  private currentInterval(): number {
    return this.difficulty().interval;
  }

  private scheduleNextSpawn(delay: number): void {
    if (!this.running) return;
    this.pendingSpawn = this.time.delayedCall(delay, () => {
      this.spawnTarget();
      this.scheduleNextSpawn(this.currentInterval());
    });
  }

  private spawnTarget(): void {
    this.clearTarget();
    const { radius } = this.difficulty();
    const { width, height } = this.scale;
    const margin = radius + 12;
    const x = Phaser.Math.Between(margin, width - margin);
    const y = Phaser.Math.Between(margin + 60, height - margin);

    this.target = this.add.circle(x, y, radius, COLOR_ACCENT);
    this.target.setStrokeStyle(3, 0xffffff);
    this.target.setInteractive({ useHandCursor: true });
    this.target.on('pointerdown', () => this.onTargetHit());

    this.cb.targetSpawn(radius);
  }

  private onTargetHit(): void {
    this.cb.targetClick();
    this.clearTarget();
    // Respawn quickly after a hit so the game feels responsive.
    if (this.pendingSpawn) {
      this.pendingSpawn.remove(false);
      this.pendingSpawn = null;
    }
    this.scheduleNextSpawn(150);
  }

  private clearTarget(): void {
    if (this.target) {
      this.target.removeAllListeners();
      this.target.destroy();
      this.target = null;
    }
  }

  private endRound(): void {
    this.running = false;
    if (this.pendingSpawn) {
      this.pendingSpawn.remove(false);
      this.pendingSpawn = null;
    }
    this.clearTarget();
    this.showOverlay(this.overOverlay);
    this.cb.roundComplete();
  }

  private buildReadyOverlay(width: number, height: number): void {
    this.readyOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -40, '30 Second Reaction', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const hint = this.add.text(0, 20, 'Tap / click START when you are ready', {
      fontSize: '18px',
      color: '#a0a0a0',
    });
    hint.setOrigin(0.5);
    const btn = this.add.rectangle(0, 90, 220, 64, COLOR_ACCENT);
    const btnText = this.add.text(0, 90, 'START', {
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.wantsStart() || this.requestBegin());
    this.readyOverlay.add([title, hint, btn, btnText]);
  }

  private buildGameOverOverlay(width: number, height: number): void {
    this.overOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -60, "Time's Up!", {
      fontSize: '40px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const score = this.add.text(0, -10, 'Final Score: 0', {
      fontSize: '24px',
      color: '#e94560',
    });
    score.setOrigin(0.5);
    score.setName('finalScoreText');
    const btn = this.add.rectangle(0, 70, 240, 64, COLOR_ACCENT);
    const btnText = this.add.text(0, 70, 'Play Again', {
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.retry());
    this.overOverlay.add([title, score, btn, btnText]);
    this.overOverlay.setVisible(false);
  }

  private showOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(true);
    overlay?.setActive(true);
  }

  private hideOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(false);
    overlay?.setActive(false);
  }

  /** Update the "Final Score" label on the game-over overlay. */
  showFinalScore(score: number): void {
    const label = this.overOverlay?.getByName('finalScoreText') as Phaser.GameObjects.Text | null;
    label?.setText(`Final Score: ${score}`);
  }
}

/**
 * ReactionGame — GameRuntime wrapper around the Phaser game.
 *
 * It owns the Phaser.Game instance, the score, and the runtime state
 * machine (idle -> mounted -> running <-> paused -> gameover -> destroyed).
 * The inner scene delegates gameplay events back through SceneCallbacks.
 */
export class ReactionGame implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private wantStart = false;
  private game: Phaser.Game | null = null;
  private scene: ReactionScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new ReactionScene(this.buildCallbacks());

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: this.config.width ?? GAME_WIDTH,
      height: this.config.height ?? GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: this.config.width ?? GAME_WIDTH,
        height: this.config.height ?? GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    this.wantStart = true;
    if (
      this.state === 'mounted' ||
      this.state === 'paused' ||
      this.state === 'gameover'
    ) {
      this.state = 'running';
      this.events.onStart?.();
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number {
    return this.score;
  }

  getState(): GameRuntimeState {
    return this.state;
  }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.scene?.resetRound();
    this.state = 'running';
    this.events.onRetry?.();
    this.wantStart = true;
    this.scene?.requestBegin();
  }

  // ---- scene -> host callbacks ----------------------------------------

  private buildCallbacks(): SceneCallbacks {
    return {
      targetClick: () => {
        this.score += 1;
        this.events.onScoreChange?.(this.score);
        this.scene?.setScoreDisplay(this.score);
        this.events.onEvent?.('target_click', { score: this.score });
      },
      wrongClick: () => {
        this.events.onEvent?.('wrong_click', {});
      },
      targetSpawn: (radius: number) => {
        this.events.onEvent?.('target_spawn', { radius });
      },
      roundComplete: () => {
        this.state = 'gameover';
        this.scene?.showFinalScore(this.score);
        this.events.onGameOver?.(this.score, { duration: DURATION_MS / 1000 });
      },
      retry: () => this.retry(),
      wantsStart: () => this.wantStart,
    };
  }
}
